import { OrganizationSettingsView } from '@dealstack/frontend/app/pages/Organization/Organization.types';
import { Tree } from '@dealstack/frontend/shared/components/Tree/Tree';
import { TreeItem } from '@dealstack/frontend/shared/components/Tree/utils';
import { useOrganizationSettingsViewLabels } from '@dealstack/frontend/app/pages/Organization/hooks/useOrganizationSettingsViewLabel';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { PredefinedIcons } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.types';

interface Props {
	title: string;
	currentView: OrganizationSettingsView;
	onViewChange: (view: OrganizationSettingsView) => void;
	enableFunds?: boolean;
	enableProgrammeDefaults?: boolean;
	enableEmailReminders?: boolean;
}

const PROGRAMME_DEFAULTS_VIEWS = [
	OrganizationSettingsView.ProgrammeDefaults,
	OrganizationSettingsView.ProgrammeDefaultsMembers,
	OrganizationSettingsView.ProgrammeDefaultsKycSettings,
	OrganizationSettingsView.ProgrammeDefaultsInvestorData,
	OrganizationSettingsView.ProgrammeDefaultsVestingTerms,
	OrganizationSettingsView.ProgrammeDefaultsMasterDocuments,
	OrganizationSettingsView.ProgrammeDefaultsTaxAndCompliance,
	OrganizationSettingsView.ProgrammeDefaultsEmailTemplates,
	OrganizationSettingsView.ProgrammeDefaultsEmailReminders,
];

const DEAL_DEFAULTS_VIEWS = [
	OrganizationSettingsView.DealDefaults,
	OrganizationSettingsView.DealDefaultsMembers,
	OrganizationSettingsView.DealDefaultsClassifiers,
	OrganizationSettingsView.DealDefaultsEmailTemplates,
];

/** Clicking a folder lands on its first child rather than a page that does not exist. */
const FOLDER_LANDING_VIEW: Partial<Record<OrganizationSettingsView, OrganizationSettingsView>> = {
	[OrganizationSettingsView.ProgrammeDefaults]: OrganizationSettingsView.ProgrammeDefaultsMembers,
	[OrganizationSettingsView.DealDefaults]: OrganizationSettingsView.DealDefaultsMembers,
};

export const OrganizationSettingsSidebar = ({
	title,
	currentView,
	onViewChange,
	enableFunds,
	enableProgrammeDefaults,
	enableEmailReminders,
}: Props) => {
	const viewLabels = useOrganizationSettingsViewLabels();

	const dealDefaultsItem: TreeItem<OrganizationSettingsView> = {
		id: OrganizationSettingsView.DealDefaults,
		name: viewLabels[OrganizationSettingsView.DealDefaults],
		icon: 'briefcase' as PredefinedIcons,
		isFolder: true,
		expanded: DEAL_DEFAULTS_VIEWS.includes(currentView),
		children: [
			{
				id: OrganizationSettingsView.DealDefaultsMembers,
				name: viewLabels[OrganizationSettingsView.DealDefaultsMembers],
				icon: 'people-users',
				selected: currentView === OrganizationSettingsView.DealDefaultsMembers,
			},
			{
				id: OrganizationSettingsView.DealDefaultsClassifiers,
				name: viewLabels[OrganizationSettingsView.DealDefaultsClassifiers],
				icon: 'document',
				selected: currentView === OrganizationSettingsView.DealDefaultsClassifiers,
			},
			{
				id: OrganizationSettingsView.DealDefaultsEmailTemplates,
				name: viewLabels[OrganizationSettingsView.DealDefaultsEmailTemplates],
				icon: 'mail' as PredefinedIcons,
				selected: currentView === OrganizationSettingsView.DealDefaultsEmailTemplates,
			},
		],
	};

	const programmeDefaultsItem: TreeItem<OrganizationSettingsView> = {
		id: OrganizationSettingsView.ProgrammeDefaults,
		name: viewLabels[OrganizationSettingsView.ProgrammeDefaults],
		icon: 'plant-grown',
		isFolder: true,
		expanded: PROGRAMME_DEFAULTS_VIEWS.includes(currentView),
		children: [
			{
				id: OrganizationSettingsView.ProgrammeDefaultsMembers,
				name: viewLabels[OrganizationSettingsView.ProgrammeDefaultsMembers],
				icon: 'people-users',
				selected: currentView === OrganizationSettingsView.ProgrammeDefaultsMembers,
			},
			{
				id: OrganizationSettingsView.ProgrammeDefaultsKycSettings,
				name: viewLabels[OrganizationSettingsView.ProgrammeDefaultsKycSettings],
				icon: 'verified-v3',
				selected: currentView === OrganizationSettingsView.ProgrammeDefaultsKycSettings,
			},
			{
				id: OrganizationSettingsView.ProgrammeDefaultsInvestorData,
				name: viewLabels[OrganizationSettingsView.ProgrammeDefaultsInvestorData],
				icon: 'user-settings',
				selected: currentView === OrganizationSettingsView.ProgrammeDefaultsInvestorData,
			},
			{
				id: OrganizationSettingsView.ProgrammeDefaultsVestingTerms,
				name: viewLabels[OrganizationSettingsView.ProgrammeDefaultsVestingTerms],
				icon: 'clock-time-watch',
				selected: currentView === OrganizationSettingsView.ProgrammeDefaultsVestingTerms,
			},
			{
				id: OrganizationSettingsView.ProgrammeDefaultsMasterDocuments,
				name: viewLabels[OrganizationSettingsView.ProgrammeDefaultsMasterDocuments],
				icon: 'document',
				selected: currentView === OrganizationSettingsView.ProgrammeDefaultsMasterDocuments,
			},
			{
				id: OrganizationSettingsView.ProgrammeDefaultsTaxAndCompliance,
				name: viewLabels[OrganizationSettingsView.ProgrammeDefaultsTaxAndCompliance],
				icon: 'percentage',
				selected:
					currentView === OrganizationSettingsView.ProgrammeDefaultsTaxAndCompliance,
			},
			{
				id: OrganizationSettingsView.ProgrammeDefaultsEmailTemplates,
				name: viewLabels[OrganizationSettingsView.ProgrammeDefaultsEmailTemplates],
				icon: 'mail' as PredefinedIcons,
				selected: currentView === OrganizationSettingsView.ProgrammeDefaultsEmailTemplates,
			},
			// Reminders have their own flag on the programme side; mirror it rather than riding
			// on the templates one.
			...(enableEmailReminders
				? [
						{
							id: OrganizationSettingsView.ProgrammeDefaultsEmailReminders,
							name: viewLabels[
								OrganizationSettingsView.ProgrammeDefaultsEmailReminders
							],
							icon: 'clock-time-watch' as PredefinedIcons,
							selected:
								currentView ===
								OrganizationSettingsView.ProgrammeDefaultsEmailReminders,
						},
					]
				: []),
		],
	};

	const treeItems: TreeItem<OrganizationSettingsView>[] = [
		{
			id: OrganizationSettingsView.General,
			name: title,
			icon: 'building-office-company-entity-line',
			selected: currentView === OrganizationSettingsView.General,
			children: [
				{
					id: OrganizationSettingsView.Members,
					name: viewLabels[OrganizationSettingsView.Members],
					icon: 'people-users',
					selected: currentView === OrganizationSettingsView.Members,
				},
				{
					id: OrganizationSettingsView.Authentication,
					name: viewLabels[OrganizationSettingsView.Authentication],
					icon: 'lock',
					selected: currentView === OrganizationSettingsView.Authentication,
				},
				...(enableFunds
					? [
							{
								id: OrganizationSettingsView.Funds,
								name: viewLabels[OrganizationSettingsView.Funds],
								icon: 'money-v3' as PredefinedIcons,
								selected: currentView === OrganizationSettingsView.Funds,
								dataTestId: TEST_ELEMENT_IDS.ORG_SIDEBAR_FUNDS,
							},
						]
					: []),
				...(enableProgrammeDefaults ? [dealDefaultsItem, programmeDefaultsItem] : []),
			],
		},
	];

	const handleItemClick = (item: TreeItem<OrganizationSettingsView>) => {
		onViewChange(FOLDER_LANDING_VIEW[item.id] ?? item.id);
	};

	return <Tree items={treeItems} onItemClick={handleItemClick} />;
};
